<template>
<div class="contribution-wrapper" :style="{
  '--chart-length': sourceNames.length
}">
  <template v-for="sourceName in sourceNames">
    <div :key="`source-name-${sourceName.id}`" style="display:flex;justify-content:center;align-items:center;gap:5px;grid-column:1/3;height:28px;line-height:28px;color:#606266;">
      <template v-if="editSourceName === sourceName.name">
        <el-select
          v-model="tmpSourceName"
          size="mini"
          style="width:100px;"
        >
          <el-option
            v-for="selectOption in selectOptions"
            :key="selectOption.id"
            :value="selectOption.id"
            :label="selectOption.name"
          />
        </el-select>
        <span class="ok-btn" @click="onOKBtnClick"><i class="el-icon-success"></i></span>
        <span class="cancel-btn" @click="onCancelBtnClick"><i class="el-icon-circle-close"></i></span>
      </template>
      <template v-else>
        <label>{{sourceName.name}}</label>
        <span class="edit-btn" @click="onEditBtnClick(sourceName)"><i class="el-icon-edit"></i></span>
      </template>
    </div>
    <factor-source-chart
      :key="`factor-source-${sourceName.id}`"
      :dimensions="factorSourceDataMap.dimensions"
      :series-map="factorSourceDataMap.seriesMap[sourceName.id]"
      style="height:300px;"
    />
    <time-source-chart
      :key="`time-source-${sourceName.id}`"
      :dimensions="timeSourceDataMap.dimensions"
      :series-map="timeSourceDataMap.seriesMap[sourceName.id]"
      style="height:300px;"
    />
  </template>
</div>
</template>

<script>
import BaseResult from './BaseResult'
import ChartStyle from '../../../../../../components/DataChart/ChartStyle'
import FactorSourceChart from '../FactorSourceChart'
import TimeSourceChart from '../TimeSourceChart'

export default {
  name: 'ContributionAnalysis',
  components: {TimeSourceChart, FactorSourceChart},
  label: '源贡献分析',
  dataKey: 'ContributionAnalysis',
  extends: BaseResult,
  data: () => ({
    sourceNames: Object.freeze([]),
    factorSourceDataMap: null,
    timeSourceDataMap: null,
    editSourceName: null,
    tmpSourceName: null,
    selectOptions: Object.freeze([])
  }),
  computed: {
    sourceMap () {
      return this.sources.reduce((map, source) => {
        map[source.id] = source
        return map
      }, {})
    }
  },
  methods: {
    buildSourceNames () {
      const {factorSources} = this.shareData
      const {sourceMap} = this
      const {COLORS} = ChartStyle
      const COLORS_LEN = COLORS.length
      let colorCount = 0
      this.sourceNames = Object.freeze([...new Set(factorSources.map(factorSource => factorSource.name))].map((sourceName, index) => {
        const source = sourceMap[sourceName]

        if (source) {
          return {
            id: sourceName,
            name: source.name,
            color: source.color
          }
        } else {
          return {
            id: sourceName,
            name: sourceName,
            color: COLORS[colorCount++ % COLORS_LEN]
          }
        }
      }))
    },
    buildFactorSourceDataMap () {
      const {sourceNames, shareData: {factorSources}, factorMap} = this
      const factorSourceDataMap = factorSources.reduce((map, data) => {
        const newData = map[data.name]
        newData.percents[data.factorId] = data.percent
        newData.concs[data.factorId] = data.conc
        return map
      }, sourceNames.reduce((map, sourceName) => {
        map[sourceName.id] = {
          ...sourceName,
          percents: {},
          concs: {}
        }
        return map
      }, {}))

      const {factorIds} = this.condition

      sourceNames.forEach(sourceName => {
        const sourceData = factorSourceDataMap[sourceName.id]
        const {percents, concs} = sourceData
        sourceData.percents = factorIds.map(factorId => percents[factorId])
        sourceData.concs = factorIds.map(factorId => concs[factorId])
      })

      this.factorSourceDataMap = Object.freeze({
        dimensions: factorIds.map(factorId => ({
          id: factorId,
          name: factorMap[factorId].name
        })),
        seriesMap: Object.freeze(factorSourceDataMap)
      })
    },
    buildTimeSourceDataMap () {
      const {sourceNames, shareData: {timeSources}} = this
      const dataTimes = [...new Set(timeSources.map(data => data.dataTime))].map(dataTime => ({
        dataTime,
        formatDataTime: new Date(dataTime).format('YYYY/MM/DD HH')
      }))
      const timeSourceDataMap = Object.freeze(this.shareData.timeSources.reduce((map, data) => {
        const newData = map[data.name]
        newData.contributions[data.dataTime] = data.contribution
        return map
      }, sourceNames.reduce((map, sourceName) => {
        map[sourceName.id] = {
          ...sourceName,
          contributions: {}
        }
        return map
      }, {})))

      sourceNames.forEach(sourceName => {
        const sourceData = timeSourceDataMap[sourceName.id]
        const {contributions} = sourceData
        sourceData.contributions = dataTimes.map(dataTime => contributions[dataTime.dataTime])
      })

      this.timeSourceDataMap = Object.freeze({
        dimensions: dataTimes,
        seriesMap: timeSourceDataMap
      })
    },
    renderData () {
      this.buildSourceNames()
      this.buildFactorSourceDataMap()
      this.buildTimeSourceDataMap()
    },
    onEditBtnClick (sourceNameConfig) {
      const {name} = sourceNameConfig
      const currentSourceMap = this.sourceNames.reduce((map, sourceNameConfig) => {
        map[sourceNameConfig.id] = sourceNameConfig
        return map
      }, {})
      const selectOptions = this.selectOptions = Object.freeze(this.sources.filter(source => !currentSourceMap[source.id]))

      if (selectOptions.length > 0) {
        this.tmpSourceName = selectOptions[0].id
      }

      this.editSourceName = name
    },
    onOKBtnClick () {
      const {shareData, editSourceName, tmpSourceName} = this

      shareData.factorSources.forEach(data => {
        if (data.name === editSourceName) {
          data.name = tmpSourceName
        }
      })

      shareData.timeSources.forEach(data => {
        if (data.name === editSourceName) {
          data.name = tmpSourceName
        }
      })

      this.editSourceName = null
      this.renderData()
    },
    onCancelBtnClick () {
      this.editSourceName = this.tmpSourceName = null
    }
  },
  created () {
    this.renderData()
  }
}
</script>

<style lang="scss" scoped>
.contribution-wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 12px;
  overflow: auto;

  > * {
    min: {
      width: 0;
      height: 0;
    };
  }
}

.ok-btn,
.cancel-btn,
.edit-btn {
  font-size: 1em;
  cursor: pointer;
}

.ok-btn {
  color: #67C23A;

  &:hover {
    color: #85ce61;
  }

  &:active {
    color: #5daf34;
  }
}

.edit-btn {
  &:hover {
    color: #66b1ff;
  }

  &:active {
    color: #3a8ee6;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/11PMF-Analysis/01PM/01Run/ResultComponents/02ContributionAnalysis.vue